import React, { useState } from 'react';
import moment, { Moment } from 'moment';
import { useAppContext } from '../../Services/AppContext';
import SendButton from '../Button/SendButton';
import EducationMaterialsFolder from './EducationMaterialsFolder';
import { useEffectAsync } from '../../helpers';
import apiClient from '../../Services/ApiClient';
import { EDUCATION_MATERIAL_FOLDERS_WITH_USER_MATERIALS } from '../EducationMaterial/EducationMaterialData';
import Loading from '../Loading';
import useNotification from '../../Hooks/useNotification.hook';
import useHealthPlan from '../../Modules/HealthPlan/Hooks/useHealthPlan';
// @ts-ignore
import styles from './EducationMaterials.module.scss';
import InputButtonGroup from '../Base/InputButtonGroup/InputButtonGroup';
import LinkButton from '../Button/LinkButton';
import { Theme } from '../../Enums/Theme.enum';
import { EducationMaterialModule } from '../../graphql/graphql';

interface EducationMaterialsProps {
	patientId: string;
	workplaceId: string;
	module?: EducationMaterialModule;
}

export default function EducationMaterials(props: EducationMaterialsProps) {
	const { patientId, workplaceId, module } = props;
	const [folders, setFolders] = useState([]);
	const { translation, dateFormatMoment } = useAppContext();
	const { showNotificationSuccess } = useNotification();
	const { isBaby, isDiabetes } = useHealthPlan();
	const [filter, setFilter] = useState<null | string>('');
	const [init, setInit] = useState(true);

	const [lastSentDate, setLastSentDate] = useState<Moment | null>(null);

	const updateItem = item => {
		const currentFolders = [...folders];
		for (const folderIndex in currentFolders) {
			if (currentFolders[folderIndex].id === item.id) {
				currentFolders[folderIndex] = item;
			}
		}

		setFolders(currentFolders);
	};

	const getModuleName = () => {
		if (module) {
			return module;
		}
		if (isBaby) {
			return 'BABY';
		}
		if (isDiabetes) {
			return 'DIABETES';
		}
	};

	// @ts-ignore
	const loadData = async () => {
		let result;
		let lastSent: Moment | null = null;
		result = await apiClient.graphql({
			query: EDUCATION_MATERIAL_FOLDERS_WITH_USER_MATERIALS,
			variables: { patientId, module: getModuleName(), fulltext: filter },
			fetchPolicy: 'network-only',
		});

		const seenMaterials: Record<string, boolean> = {};
		const sendMaterials: Record<string, boolean> = {};
		for (const folder of result?.data?.educationMaterialFolders?.data) {
			for (const material of folder?.educationMaterials) {
				seenMaterials[material.id] = material.seen;
				sendMaterials[material.id] = material.send;
				if (material?.seenAt) {
					if (!lastSent || +new Date(lastSent) < +new Date(material.seenAt)) {
						lastSent = moment(material.seenAt);
					}
				}
			}
		}

		const currentFolders = result?.data?.educationMaterialFolders?.data.map(
			d => ({
				...d,
				educationMaterials: d.educationMaterials.map(e => ({
					...e,
					active: false,
					seen: seenMaterials[e.id] || false,
					sent: sendMaterials[e.id] || false,
				})),
			}),
		);

		setFolders(currentFolders);
		setLastSentDate(lastSent);
	};

	// @ts-ignore
	useEffectAsync(async () => {
		await loadData();
		if (!filter) {
			setInit(false);
			setInit(true);
		}
	}, [filter]);

	// @ts-ignore
	const handleSendMaterials = async function () {
		const activeMaterials = [];
		folders.map(folder => {
			folder.educationMaterials.map(material => {
				if (material.active) {
					activeMaterials.push(material.id);
				}
				return material;
			});
			return folder;
		});

		await apiClient.post('/education-materials/assign/' + patientId + '/ids', {
			ids: activeMaterials,
			note: '',
		});

		showNotificationSuccess({
			content: translation['component.educationMaterial.materialsSent'],
		});

		await loadData();
	};

	const handleSearch = (fulltext: string) => {
		setFilter(fulltext);
	};

	if (!folders) {
		return (
			<div className={'py-5'}>
				<Loading />
			</div>
		);
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.options}>
				<div className={styles.leftPart}>
					<div className={styles.title}>
						{lastSentDate !== null && (
							<>
								<span>{translation['base.lastMaterialsSentAt']}:</span>{' '}
								{lastSentDate.format(dateFormatMoment)}{' '}
								{lastSentDate.format('H:m')}
							</>
						)}
					</div>
					<div className={styles.filter}>
						{init && (
							<InputButtonGroup
								placeholder={'base.enterNameOfFile'}
								onSearch={handleSearch}
							/>
						)}
						{filter && (
							<LinkButton
								onClick={() => setFilter(null)}
								title={'base.removeFilter'}
								theme={Theme.RED}
								className={styles.resetFilterButton}
							/>
						)}
					</div>
				</div>
				<SendButton onClick={handleSendMaterials} />
			</div>
			<div className={styles.list}>
				{folders.map(folder => (
					<div key={folder.id}>
						<EducationMaterialsFolder data={folder} onUpdateItem={updateItem} />
					</div>
				))}
			</div>
		</div>
	);
}
